/**
 * NotificationsPage — Full-page notification center.
 * Accessible from Dashboard bell icon at /notifications.
 *
 * Shows: invites (accept/decline), role changes, permission updates, team activity.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Bell, Mail, UserPlus, UserMinus, Shield, Settings, ChevronLeft,
  CheckCircle2, XCircle, Check, Trash2, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNotifications } from '@/contexts/NotificationContext';
import { acceptInvite, declineInvite } from '@/services/teamService';
import { Logo1895 } from '@/components/Logo1895';

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'invites', label: 'Invites' },
  { id: 'team', label: 'Team Activity' },
];

const INVITE_TYPES = ['invite'];
const TEAM_TYPES = ['member_joined', 'member_removed', 'role_change', 'permission_change', 'invite_declined'];

function getNotificationIcon(type) {
  switch (type) {
    case 'invite': return <Mail size={18} className="text-[var(--color-gold-400)]" />;
    case 'member_joined': return <UserPlus size={18} className="text-emerald-400" />;
    case 'member_removed': return <UserMinus size={18} className="text-red-400" />;
    case 'role_change': return <Shield size={18} className="text-blue-400" />;
    case 'permission_change': return <Settings size={18} className="text-purple-400" />;
    case 'invite_declined': return <XCircle size={18} className="text-orange-400" />;
    default: return <Bell size={18} className="text-[var(--text-muted)]" />;
  }
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

function NotificationItem({ notification, onAccept, onDecline, onMarkRead, onDelete, onNavigate }) {
  const [processing, setProcessing] = useState(false);
  const isInvite = notification.type === 'invite' && !notification.read;

  const handleAccept = async () => {
    if (!notification.invite_token) return;
    setProcessing(true);
    try {
      await onAccept(notification.invite_token, notification.id);
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!notification.invite_token) return;
    setProcessing(true);
    try {
      await onDecline(notification.invite_token, notification.id);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`group flex items-start gap-3 p-4 rounded-xl border transition-colors ${
        notification.read
          ? 'border-transparent bg-[var(--bg-surface)]/50'
          : 'border-[var(--color-gold-500)]/20 bg-[var(--bg-elevated)]'
      }`}
    >
      {/* Unread dot */}
      <div className="flex-shrink-0 mt-1">
        {!notification.read ? (
          <div className="w-2 h-2 rounded-full bg-[var(--color-gold-400)]" />
        ) : (
          <div className="w-2 h-2" />
        )}
      </div>

      {/* Icon */}
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[var(--bg-app)] flex items-center justify-center">
        {getNotificationIcon(notification.type)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className={`text-sm font-medium ${notification.read ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'}`}>
              {notification.title}
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-0.5 line-clamp-2">
              {notification.message}
            </p>
          </div>
          <span className="text-[10px] text-[var(--text-disabled)] whitespace-nowrap flex-shrink-0">
            {timeAgo(notification.created_at)}
          </span>
        </div>

        {/* Action buttons for invites */}
        {isInvite && notification.invite_token && (
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleAccept}
              disabled={processing}
              className="h-7 px-3 text-xs bg-[var(--color-gold-500)] hover:bg-[var(--color-gold-400)] text-black font-medium"
            >
              <CheckCircle2 size={14} className="mr-1" />
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDecline}
              disabled={processing}
              className="h-7 px-3 text-xs border-[var(--border-default)] text-[var(--text-secondary)]"
            >
              <XCircle size={14} className="mr-1" />
              Decline
            </Button>
          </div>
        )}

        {/* Navigate to project */}
        {notification.project_id && notification.type !== 'invite' && (
          <button
            onClick={() => onNavigate(notification)}
            className="flex items-center gap-1 mt-2 text-xs text-[var(--color-gold-400)] hover:text-[var(--color-gold-300)] transition-colors"
          >
            View project <ArrowRight size={12} />
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        {!notification.read && (
          <button
            onClick={() => onMarkRead(notification.id)}
            className="p-1 rounded hover:bg-[var(--bg-app)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
            title="Mark as read"
          >
            <Check size={14} />
          </button>
        )}
        <button
          onClick={() => onDelete(notification.id)}
          className="p-1 rounded hover:bg-[var(--bg-app)] text-[var(--text-muted)] hover:text-red-400"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </motion.div>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const {
    persistent: notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification,
    fetchNotifications,
  } = useNotifications();

  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const filtered = notifications.filter((n) => {
    if (filter === 'invites') return INVITE_TYPES.includes(n.type);
    if (filter === 'team') return TEAM_TYPES.includes(n.type);
    return true;
  });

  const handleAccept = useCallback(async (token, notificationId) => {
    try {
      const result = await acceptInvite(token);
      await markAsRead(notificationId);
      await fetchNotifications();
      toast.success(result.message);
      if (result.project_id) {
        navigate(`/project/${result.project_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    }
  }, [markAsRead, fetchNotifications, navigate]);

  const handleDecline = useCallback(async (token, notificationId) => {
    try {
      await declineInvite(token);
      await markAsRead(notificationId);
      await fetchNotifications();
      toast.success('Invitation declined');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline invite');
    }
  }, [markAsRead, fetchNotifications]);

  const handleNavigate = useCallback((notification) => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.project_id) {
      navigate(`/project/${notification.project_id}`);
    }
  }, [markAsRead, navigate]);

  return (
    <div className="h-screen flex flex-col bg-[var(--bg-app)] text-[var(--text-primary)] overflow-hidden">
      {/* Header */}
      <header className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]/80 backdrop-blur-xl sticky top-0 z-50 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 -ml-2 rounded-lg hover:bg-[var(--bg-elevated)] transition-colors"
            >
              <ChevronLeft size={20} className="text-[var(--text-secondary)]" />
            </button>
            <Logo1895 variant="icon" width={28} />
            <h1 className="text-lg font-semibold">Notifications</h1>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-[var(--color-gold-500)]/20 text-[var(--color-gold-400)] text-xs font-medium">
                {unreadCount} unread
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              Mark all read
            </Button>
          )}
        </div>
      </header>

      {/* Filter tabs */}
      <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]/40">
        <div className="max-w-3xl mx-auto px-6 flex gap-1 py-2">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === tab.id
                  ? 'bg-[var(--color-gold-500)]/20 text-[var(--color-gold-400)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notification list */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-4 space-y-2">
          <AnimatePresence mode="popLayout">
            {filtered.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-20 text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
                  <Bell size={28} className="text-[var(--text-disabled)]" />
                </div>
                <p className="text-sm text-[var(--text-muted)]">
                  {filter === 'all' ? 'No notifications yet' : `No ${filter === 'invites' ? 'invite' : 'team'} notifications`}
                </p>
                <p className="text-xs text-[var(--text-disabled)] mt-1">
                  You'll be notified when someone invites you to a project or changes your access.
                </p>
              </motion.div>
            ) : (
              filtered.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  onMarkRead={markAsRead}
                  onDelete={clearNotification}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
